import {Component, Host, h, Listen, Prop, Event, type EventEmitter} from '@stencil/core';
import type {FilterEventModel, FilterNode} from '../../filter-node';
import {
  type DefaultFilterFacetChangedEvent,
  type DefaultFiltersSelectedAnalyticsData
} from './default-filter-selected-events.model';
import {FILTERS_ID_SEPARATOR} from '../../../../../constants/filterConstants';
import {type ObservableMap} from '@stencil/store';
import {cloneObjectWithNoReference} from '../../../../../utils/helpers/clone-object-with-no-reference';
import type {InSearchEventModel} from '../../models/in-search-event.model';

@Component({
  tag: 'uiaas-search-default-analytics-filters-wrapper',
  shadow: false,
})
export class DefaultAnalyticsFiltersWrapper {
  @Prop() readonly tabName: string;
  @Prop() readonly store: ObservableMap<DefaultFiltersSelectedAnalyticsData>;

  @Event({
    eventName: 'facest:onResetFilterDeselect',
    composed: true,
    cancelable: true,
    bubbles: true
  }) onResetFilterDeselecteEmitter: EventEmitter<DefaultFilterFacetChangedEvent>;

  @Event({
    eventName: 'facest:onResetAll',
    composed: true,
    cancelable: true,
    bubbles: true
  }) onResetAllEmitter: EventEmitter<unknown>;

  @Event({
    eventName: 'facest:onFilterChange',
    composed: true,
    cancelable: true,
    bubbles: true
  }) onFilterChangeEmitter: EventEmitter<unknown>;

  @Event({
    eventName: 'facest:onInsearchInput',
    composed: true,
    cancelable: true,
    bubbles: true
  }) onInSearchInputEmitter: EventEmitter<InSearchEventModel>;


  @Listen('filter:did:change')
  handleFilterDidChange(event: CustomEvent) {
      const {filterNode, facetCategoryDisplayName} = event.detail;
      const selectedFilters: FilterNode[] = [...this.store.get('selectedFilters')];
      const indexSelected = selectedFilters.findIndex(filter => filter.uniqueId === filterNode.uniqueId);

      if(filterNode.selected) {
        if (indexSelected === -1) {
          selectedFilters.push(filterNode);
        }
      }

      if(!filterNode.selected) {
        if (indexSelected !== -1) {
          selectedFilters.splice(indexSelected, 1);
        }
      }

      this.store.set( 'selectedFilters', selectedFilters);
      this.onFilterDidChange({...filterNode, facetCategoryDisplayName});
  }

  @Listen('search:reset')
  onReset() {
      this.onResetAllEmitter.emit({tabName: this.tabName});
      this.emitFiltersChangeEvents();
      this.store.reset();
  }

  @Listen('insearch:input')
  onInSearchInput(event: CustomEvent) {
      this.onInSearchInputEmitter.emit(
        {
          category: 'search-result-card',
          action: 'click',
          clickedArea: 'insearch',
          eventName: event.detail.event,
          siteSearchKeyword: event.detail.siteSearchKeyword,
          tabName: this.tabName,
          facetCategory: event.detail.facetCategory
        });
  }

  @Listen('insearch:characteristicsInput')
  onInSearchCharacteristicsInput(event: CustomEvent) {
      this.onInSearchInputEmitter.emit(
        {
          category: 'search-filters',
          action: 'click',
          clickedArea: 'inCharacteristicsSearch',
          eventName: event.detail.event,
          siteSearchKeyword: event.detail.siteSearchKeyword,
          tabName: this.tabName,
          facetCategory: event.detail.facetCategory
        });
  }

  private onFilterDidChange(filterNode: FilterEventModel) {
      this.onFilterChangeEmitter.emit(cloneObjectWithNoReference({...{tabName: this.tabName},...filterNode}));
  }

  private emitFiltersChangeEvents(): void {
      this.store.state.selectedEventsList.forEach(
        eventData => this.onResetFilterDeselecteEmitter.emit(eventData)
      );
  }

  componentWillLoad() {
      this.store.onChange('selectedFilters', (value: FilterNode[]) => {
          this.store.state.selectedEventsList = [...value].map(filterNode => {
              const parsedUniqueId = filterNode.uniqueId.split(FILTERS_ID_SEPARATOR);
              return {
                event: 'filterClick',
                actionType: 'Filter deselected',
                filterCategory: decodeURIComponent(parsedUniqueId[0]),
                filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
                tabName: this.tabName,
                clickedArea: 'resetAll'
              };
          });
      });
  }

  render() {
      return (
        <Host>
          <slot></slot>
        </Host>
      );
  }

  disconnectedCallback() {
      this.store.reset();
  }
}
